// HospitalAdminDashboard.jsx — with mobile support matching SuperAdmin style
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { API } from "../config";

const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDob  = (v) => v ? new Date(v).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const COLORS = ["#3b82f6","#8b5cf6","#06b6d4","#10b981","#f59e0b","#ef4444","#ec4899"];
const pickColor = name => COLORS[(name||"").charCodeAt(0) % COLORS.length];

const NAV = [
  { id: "overview",     icon: "dashboard",          label: "Overview"      },
  { id: "subadmins",    icon: "supervisor_account", label: "Sub-Admins"    },
  { id: "users",        icon: "people",             label: "Medical Staff" },
  { id: "patients",     icon: "folder_shared",      label: "Patients"      },
  { id: "assignments",  icon: "assignment_ind",     label: "Assignments"   },
  { id: "devices",      icon: "devices",            label: "Devices"       },
  { id: "chat",         icon: "chat_bubble",        label: "Chat"          },
];

// ════════════════════════════════════════════════════════════════════════════
// AVATAR
// ════════════════════════════════════════════════════════════════════════════
function Avatar({ name, size = 36, bg = "#3b82f6" }) {
  const initials = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: size * .35, flexShrink: 0, lineHeight: 1 }}>
      {initials}
    </div>
  );
}

const fmtTime = d => {
  if (!d) return "";
  const date = new Date(d), now = new Date();
  const diff = Math.floor((now - date) / 86400000);
  if (diff === 0) return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (diff === 1) return "Yesterday";
  if (diff < 7)  return date.toLocaleDateString("en-GB", { weekday: "short" });
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const editSecondsLeft = createdAt => {
  const age = (Date.now() - new Date(createdAt).getTime()) / 1000;
  return Math.max(0, 900 - age);
};

// ════════════════════════════════════════════════════════════════════════════
// MESSAGE BUBBLE
// ════════════════════════════════════════════════════════════════════════════
function MessageBubble({ m, isMe, peer, userId, onReply, onDelete, onEdit }) {
  const [hover,    setHover]    = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(m.text || "");
  const [editLeft, setEditLeft] = useState(() => Math.floor(editSecondsLeft(m.created_at)));
  const editRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isMe || m.deleted) return;
    const id = setInterval(() => {
      const s = Math.floor(editSecondsLeft(m.created_at));
      setEditLeft(s);
      if (s === 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [isMe, m.created_at, m.deleted]);

  useEffect(() => {
    if (!menuOpen) return;
    const h = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  useEffect(() => { if (editMode) { editRef.current?.focus(); editRef.current?.select(); } }, [editMode]);

  const canEdit   = isMe && !m.deleted && editLeft > 0 && !m.optimistic;
  const canDelete = isMe && !m.deleted && !m.optimistic;

  const submitEdit = () => {
    if (editText.trim() && editText.trim() !== m.text) onEdit(m.id, editText.trim());
    setEditMode(false);
  };
  const handleEditKey = e => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitEdit(); }
    if (e.key === "Escape") { setEditMode(false); setEditText(m.text || ""); }
  };
  const fmtCountdown = s => { const mm = Math.floor(s / 60); const ss = s % 60; return `${mm}:${String(ss).padStart(2,"0")}`; };

  const replyAuthorLabel = () => {
    if (!m.reply_to_sender_name) return isMe ? peer.username : "You";
    if (isMe) return m.reply_to_sender_name === peer.username ? peer.username : "You";
    return m.reply_to_sender_name === peer.username ? "You" : m.reply_to_sender_name;
  };

  if (m.deleted) {
    return (
      <div style={{display:"flex",flexDirection:isMe?"row-reverse":"row",alignItems:"center",gap:".5rem",marginBottom:".375rem"}}>
        {!isMe && <Avatar name={peer.username} size={26} bg={pickColor(peer.username)}/>}
        <div style={{display:"flex",alignItems:"center",gap:".375rem",background:"#f3f4f6",borderRadius:"1rem",padding:".5rem .9rem",opacity:.65}}>
          <span className="ms" style={{fontSize:".875rem",color:"#9ca3af"}}>block</span>
          <span style={{fontSize:".8125rem",color:"#9ca3af",fontStyle:"italic"}}>Message deleted</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:isMe?"row-reverse":"row",alignItems:"flex-end",gap:".5rem",marginBottom:".25rem"}}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {!isMe && <div style={{width:26,flexShrink:0}}><Avatar name={peer.username} size={26} bg={pickColor(peer.username)}/></div>}
      <div style={{maxWidth:"65%",display:"flex",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start",position:"relative"}}>
        {m.reply_to_id && (
          <div style={{display:"flex",flexDirection:isMe?"row-reverse":"row",alignItems:"stretch",gap:0,marginBottom:"2px",opacity:.85}}>
            <div style={{width:3,borderRadius:"9999px",background:isMe?"rgba(255,255,255,0.5)":"#93c5fd",flexShrink:0,marginRight:isMe?0:"6px",marginLeft:isMe?"6px":0}}/>
            <div style={{background:isMe?"rgba(59,130,246,0.18)":"#e8f3ff",borderRadius:isMe?"10px 10px 0 10px":"10px 10px 10px 0",padding:"5px 10px",maxWidth:220,minWidth:60}}>
              <span style={{display:"block",fontSize:".68rem",fontWeight:700,color:isMe?"rgba(255,255,255,0.9)":"#2563eb",marginBottom:"1px",whiteSpace:"nowrap"}}>{replyAuthorLabel()}</span>
              <span style={{display:"block",fontSize:".72rem",color:isMe?"rgba(255,255,255,0.75)":"#4b6fa8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {m.reply_to_deleted ? "🚫 Message deleted" : (m.reply_to_text || "").slice(0,80) + ((m.reply_to_text||"").length > 80 ? "…" : "")}
              </span>
            </div>
          </div>
        )}
        <div style={{background:isMe?"#3b82f6":"#fff",color:isMe?"#fff":"#111827",borderRadius:isMe?"1.125rem 1.125rem 0.25rem 1.125rem":"1.125rem 1.125rem 1.125rem 0.25rem",padding:".625rem .9rem",fontSize:".875rem",lineHeight:1.5,boxShadow:"0 1px 2px rgba(0,0,0,.07)",opacity:m.optimistic?.7:1,border:isMe?"none":"0.5px solid #e5e7eb",wordBreak:"break-word",position:"relative"}}>
          {editMode ? (
            <div>
              <textarea ref={editRef} value={editText} onChange={e => setEditText(e.target.value)} onKeyDown={handleEditKey} rows={2}
                style={{width:"100%",minWidth:180,background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.5)",borderRadius:".5rem",color:"#fff",fontSize:".875rem",fontFamily:"inherit",padding:".35rem .5rem",resize:"none",outline:"none",lineHeight:1.4,boxSizing:"border-box"}}/>
              <div style={{display:"flex",gap:".375rem",marginTop:".375rem",justifyContent:"flex-end"}}>
                <button onClick={() => { setEditMode(false); setEditText(m.text||""); }} style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:".5rem",color:"#fff",fontSize:".75rem",padding:".25rem .625rem",cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                <button onClick={submitEdit} style={{background:"#fff",border:"none",borderRadius:".5rem",color:"#3b82f6",fontSize:".75rem",fontWeight:700,padding:".25rem .625rem",cursor:"pointer",fontFamily:"inherit"}}>Save</button>
              </div>
            </div>
          ) : (
            <>
              <p style={{margin:"0 0 .25rem",lineHeight:1.55}}>{m.text}</p>
              <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:".3rem",flexWrap:"wrap"}}>
                {m.edited_at && !m.optimistic && <span style={{fontSize:".6rem",color:isMe?"rgba(255,255,255,.6)":"#9ca3af",fontStyle:"italic"}}>Edited</span>}
                <span style={{fontSize:".62rem",color:isMe?"rgba(255,255,255,.65)":"#9ca3af"}}>{fmtTime(m.created_at)}</span>
                {isMe && <span className="ms" style={{fontSize:".75rem",color:m.seen?"#93c5fd":"rgba(255,255,255,.5)"}}>{m.seen?"done_all":"done"}</span>}
              </div>
            </>
          )}
        </div>
        {!editMode && hover && (
          <div style={{position:"absolute",[isMe?"left":"right"]:"-76px",bottom:"6px",display:"flex",flexDirection:"row",gap:"4px",alignItems:"center",zIndex:20}}>
            <button onClick={() => onReply({ id: m.id, text: m.text, sender: isMe ? "You" : peer.username })} title="Reply"
              style={{width:28,height:28,borderRadius:"50%",background:"#e5e7eb",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#6b7280",padding:0,boxShadow:"0 1px 3px rgba(0,0,0,.1)"}}
              onMouseEnter={e=>e.currentTarget.style.background="#d1d5db"} onMouseLeave={e=>e.currentTarget.style.background="#e5e7eb"}>
              <span className="ms" style={{fontSize:".875rem"}}>reply</span>
            </button>
            {isMe && (canEdit || canDelete) && (
              <div ref={menuRef} style={{position:"relative"}}>
                <button onClick={() => setMenuOpen(o=>!o)} title="Options"
                  style={{width:28,height:28,borderRadius:"50%",background:"#e5e7eb",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#6b7280",padding:0,boxShadow:"0 1px 3px rgba(0,0,0,.1)"}}
                  onMouseEnter={e=>e.currentTarget.style.background="#d1d5db"} onMouseLeave={e=>e.currentTarget.style.background="#e5e7eb"}>
                  <span className="ms" style={{fontSize:".875rem"}}>more_vert</span>
                </button>
                {menuOpen && (
                  <div style={{position:"absolute",bottom:"calc(100% + 6px)",left:"50%",transform:"translateX(-50%)",background:"#fff",borderRadius:"1rem",boxShadow:"0 8px 28px rgba(0,0,0,.16)",padding:".375rem",minWidth:160,zIndex:60,border:"0.5px solid #e5e7eb"}}>
                    {canEdit && (
                      <button onClick={() => { setMenuOpen(false); setEditMode(true); }}
                        style={{display:"flex",alignItems:"center",gap:".5rem",width:"100%",padding:".5rem .75rem",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:".8125rem",fontWeight:600,color:"#374151",borderRadius:".625rem",textAlign:"left"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#f3f4f6"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <span className="ms" style={{fontSize:"1rem",color:"#3b82f6"}}>edit</span>Edit
                        <span style={{marginLeft:"auto",fontSize:".68rem",color:"#9ca3af",fontVariantNumeric:"tabular-nums"}}>{fmtCountdown(editLeft)}</span>
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => { setMenuOpen(false); onDelete(m.id); }}
                        style={{display:"flex",alignItems:"center",gap:".5rem",width:"100%",padding:".5rem .75rem",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:".8125rem",fontWeight:600,color:"#ef4444",borderRadius:".625rem",textAlign:"left"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#fef2f2"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <span className="ms" style={{fontSize:"1rem"}}>delete</span>Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CONTACT ROW
// ════════════════════════════════════════════════════════════════════════════
function ContactRow({ c, userId, peer, unread, onOpen }) {
  const isActive = peer?.id === c.id;
  const cnt = unread[c.id] || 0;
  const col = pickColor(c.username);
  return (
    <div onClick={() => onOpen(c)} style={{padding:".75rem 1rem",cursor:"pointer",display:"flex",alignItems:"center",gap:".75rem",background:isActive?"#eff6ff":"transparent",borderLeft:isActive?"3px solid #3b82f6":"3px solid transparent",borderBottom:"1px solid #f9fafb",transition:"all .12s"}}>
      <div style={{position:"relative",flexShrink:0}}>
        <Avatar name={c.username} size={38} bg={col}/>
        <span style={{position:"absolute",bottom:1,right:1,width:8,height:8,borderRadius:"50%",background:"#22c55e",border:"2px solid #fff"}}/>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:".25rem"}}>
          <p style={{fontWeight:isActive||cnt>0?700:600,color:"#111827",margin:0,fontSize:".875rem",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.username}</p>
          {c.last_message_at && <span style={{fontSize:".65rem",color:"#9ca3af",flexShrink:0}}>{fmtTime(c.last_message_at)}</span>}
        </div>
        <p style={{fontSize:".75rem",color:cnt>0?"#3b82f6":"#9ca3af",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:cnt>0?600:400}}>
          {c.last_message?(c.last_message_from_me?"You: ":"")+c.last_message:c.role}
        </p>
      </div>
      {cnt > 0 && <span style={{background:"#3b82f6",color:"#fff",fontSize:".65rem",fontWeight:700,minWidth:18,height:18,borderRadius:"9999px",display:"flex",alignItems:"center",justifyContent:"center",padding:"0 5px",flexShrink:0}}>{cnt}</span>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// HOSPITAL CHAT
// ════════════════════════════════════════════════════════════════════════════
function HospitalChat({ userId, hospitalId }) {
  const [contacts,   setContacts]   = useState([]);
  const [peer,       setPeer]       = useState(null);
  const [messages,   setMessages]   = useState([]);
  const [input,      setInput]      = useState("");
  const [unread,     setUnread]     = useState({});
  const [search,     setSearch]     = useState("");
  const [sending,    setSending]    = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const endRef   = useRef(null);
  const inputRef = useRef(null);
  const pollRef  = useRef(null);

  const fetchContacts = useCallback(async () => {
    try { const r = await fetch(`${API}/chat/contacts?userId=${userId}&hospitalId=${hospitalId}`); if (!r.ok) throw new Error(); const data = await r.json(); setContacts(Array.isArray(data)?data:[]); } catch { setContacts([]); }
  }, [userId, hospitalId]);

  const fetchMessages = useCallback(async (peerId) => {
    if (!peerId) return;
    try { const r = await fetch(`${API}/chat/messages?userId=${userId}&peerId=${peerId}`); if (!r.ok){if(r.status===403)setPeer(null);return;} const data=await r.json(); setMessages(Array.isArray(data)?data:[]); } catch { setMessages([]); }
  }, [userId]);

  const fetchUnread = useCallback(async () => {
    try { const r=await fetch(`${API}/chat/unread?userId=${userId}`); if(!r.ok)throw new Error(); const data=await r.json(); const map={}; (Array.isArray(data)?data:[]).forEach(row=>{map[row.sender_id]=+row.count;}); setUnread(map); } catch {}
  }, [userId]);

  useEffect(() => {
    fetchContacts(); fetchUnread();
    pollRef.current = setInterval(() => { fetchContacts(); fetchUnread(); if (peer) fetchMessages(peer.id); }, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchContacts, fetchUnread, peer, fetchMessages]);

  const markSeen = useCallback(async (peerId, msgs) => {
    const unseen = (msgs||messages).filter(m=>String(m.sender_id)===String(peerId)&&!m.seen&&!m.deleted);
    if (!unseen.length) return;
    try { await Promise.all(unseen.map(m=>fetch(`${API}/chat/messages/${m.id}/seen`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({actorId:parseInt(userId),text:m.text})}))); setMessages(prev=>prev.map(m=>String(m.sender_id)===String(peerId)?{...m,seen:1}:m)); setUnread(prev=>({...prev,[peerId]:0})); } catch {}
  }, [messages, userId]);

  useEffect(()=>{if(peer&&messages.length)markSeen(peer.id,messages);},[messages.length,peer?.id]); // eslint-disable-line
  useEffect(()=>{endRef.current?.scrollIntoView({behavior:"smooth"});},[messages]);

  const openChat = async (contact) => {
    setPeer(contact); await fetchMessages(contact.id); setTimeout(()=>inputRef.current?.focus(),100);
  };

  const sendMessage = async () => {
    const text=input.trim();if(!text||!peer||sending)return;setInput("");setSending(true);
    const opt={id:"opt_"+Date.now(),sender_id:parseInt(userId),receiver_id:peer.id,text,seen:0,created_at:new Date().toISOString(),optimistic:true,reply_to_id:replyingTo?.id||null,reply_to_text:replyingTo?.text||"",reply_to_sender_name:replyingTo?.sender||"",reply_to_deleted:0,deleted:0,edited_at:null};
    setMessages(prev=>[...prev,opt]); setReplyingTo(null);
    try{const r=await fetch(`${API}/chat/messages`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({sender_id:parseInt(userId),receiver_id:peer.id,text,reply_to_id:replyingTo?.id||null})});
      if(r.ok){const saved=await r.json();setMessages(prev=>prev.map(m=>m.id===opt.id?saved:m));}
      else{setMessages(prev=>prev.filter(m=>m.id!==opt.id));const err=await r.json();alert(err.error||"Failed");}
    }catch{setMessages(prev=>prev.filter(m=>m.id!==opt.id));alert("Failed");}finally{setSending(false);}
  };

  const handleDelete=async(msgId)=>{if(!window.confirm("Delete this message?"))return;try{const r=await fetch(`${API}/chat/messages/${msgId}`,{method:"DELETE",headers:{"Content-Type":"application/json"},body:JSON.stringify({actorId:parseInt(userId)})});if(r.ok){setMessages(prev=>prev.map(m=>m.id===msgId?{...m,deleted:1,text:""}:m));}else{const d=await r.json();alert(d.error||"Could not delete");}}catch{alert("Failed");}};
  const handleEdit=async(msgId,newText)=>{try{const r=await fetch(`${API}/chat/messages/${msgId}/edit`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({actorId:parseInt(userId),text:newText})});const d=await r.json();if(r.ok){setMessages(prev=>prev.map(m=>m.id===msgId?{...m,text:newText,edited_at:d.edited_at}:m));}else{alert(d.error||"Could not edit");}}catch{alert("Failed");}};
  const handleKey=e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();}};

  const totalUnread=Object.values(unread).reduce((a,b)=>a+b,0);
  const filtContacts=contacts.filter(c=>String(c.id)!==String(userId)&&(!search||c.username.toLowerCase().includes(search.toLowerCase())));

  const grouped=[];let lastDate=null;
  messages.forEach(m=>{const d=new Date(m.created_at).toLocaleDateString();if(d!==lastDate){grouped.push({type:"date",label:d===new Date().toLocaleDateString()?"Today":d});lastDate=d;}grouped.push({type:"msg",msg:m});});

  return (
    <div style={{display:"grid",gridTemplateColumns: peer ? "0 1fr" : "1fr 0",gap:0,background:"#fff",borderRadius:"1.5rem",boxShadow:"0 4px 20px -2px rgba(0,0,0,.07)",overflow:"hidden",minHeight:500}} className="chat-grid">
      {/* ── Contacts sidebar ── */}
      <div style={{borderRight:"1px solid #f3f4f6",display:"flex",flexDirection:"column"}} className={`chat-contacts ${peer ? "chat-contacts-hidden" : ""}`}>
        <div style={{padding:"1.125rem 1rem",borderBottom:"1px solid #f3f4f6"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:".875rem"}}>
            <h2 style={{fontWeight:800,fontSize:"1rem",color:"#111827",margin:0}}>Messages</h2>
            {totalUnread>0&&<span style={{background:"#ef4444",color:"#fff",fontSize:".65rem",fontWeight:700,padding:"2px 8px",borderRadius:"9999px"}}>{totalUnread}</span>}
          </div>
          <div style={{position:"relative"}}>
            <span className="ms" style={{position:"absolute",left:".75rem",top:"50%",transform:"translateY(-50%)",color:"#9ca3af",fontSize:"1rem"}}>search</span>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…" style={{width:"100%",boxSizing:"border-box",padding:".45rem .75rem .45rem 2.25rem",border:"1.5px solid #e5e7eb",borderRadius:".875rem",fontSize:".8125rem",fontFamily:"inherit",outline:"none",background:"#f9fafb",color:"#111827"}}/>
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto"}}>
          {filtContacts.length===0
            ? <div style={{padding:"2rem 1rem",textAlign:"center"}}><span className="ms" style={{fontSize:"2rem",color:"#e5e7eb",display:"block",marginBottom:".5rem"}}>chat_bubble</span><p style={{color:"#9ca3af",fontSize:".8125rem",margin:0}}>No contacts yet</p></div>
            : filtContacts.map(c=><ContactRow key={c.id} c={c} userId={userId} peer={peer} unread={unread} onOpen={openChat}/>)
          }
        </div>
      </div>

      {/* ── Message panel ── */}
      {!peer?(
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"1rem",background:"#fafafa",padding:"2rem"}} className="chat-empty">
          <div style={{width:72,height:72,borderRadius:"20px",background:"linear-gradient(135deg,#eff6ff,#dbeafe)",display:"flex",alignItems:"center",justifyContent:"center"}}><span className="ms" style={{color:"#3b82f6",fontSize:"2rem"}}>chat_bubble</span></div>
          <h3 style={{fontWeight:700,color:"#111827",margin:0,fontSize:"1.125rem"}}>Hospital Chat</h3>
          <p style={{color:"#6b7280",fontSize:".875rem",textAlign:"center",maxWidth:280,margin:0}}>Select a colleague to start chatting.</p>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column"}}>
          <div style={{padding:".875rem 1.25rem",borderBottom:"1px solid #f3f4f6",display:"flex",alignItems:"center",gap:".875rem",background:"#fff"}}>
            <button onClick={()=>{setPeer(null);setReplyingTo(null);}} style={{width:32,height:32,borderRadius:"8px",border:"none",background:"#f3f4f6",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#6b7280"}}><span className="ms" style={{fontSize:".875rem"}}>arrow_back</span></button>
            <div style={{position:"relative"}}><Avatar name={peer.username} size={36} bg={pickColor(peer.username)}/><span style={{position:"absolute",bottom:1,right:1,width:7,height:7,borderRadius:"50%",background:"#22c55e",border:"2px solid #fff"}}/></div>
            <div style={{flex:1}}><p style={{fontWeight:700,color:"#111827",margin:0,fontSize:".9375rem"}}>{peer.username}</p><p style={{fontSize:".72rem",color:"#6b7280",margin:0,textTransform:"capitalize"}}>{peer.role}</p></div>
            <div style={{display:"flex",alignItems:"center",gap:".375rem",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:"9999px",padding:".3rem .7rem"}} className="edit-pill"><span className="ms" style={{fontSize:".8rem",color:"#16a34a"}}>edit</span><span style={{fontSize:".68rem",color:"#16a34a",fontWeight:600}}>15 min</span></div>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"1.25rem 1.25rem 1rem",display:"flex",flexDirection:"column",gap:".125rem",background:"#fafafa",minHeight:300,maxHeight:420}}>
            {grouped.length===0&&<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",gap:".75rem",opacity:.6}}><Avatar name={peer.username} size={56} bg={pickColor(peer.username)}/><p style={{color:"#9ca3af",fontSize:".875rem"}}>Start a conversation with {peer.username}</p></div>}
            {grouped.map((item,i)=>{
              if(item.type==="date")return(<div key={"d"+i} style={{textAlign:"center",margin:".875rem 0 .375rem"}}><span style={{background:"#e5e7eb",color:"#6b7280",fontSize:".68rem",fontWeight:600,padding:"3px 12px",borderRadius:"9999px"}}>{item.label}</span></div>);
              const m=item.msg;const isMe=String(m.sender_id)===String(userId);
              return(<MessageBubble key={m.id||i} m={m} isMe={isMe} peer={peer} userId={userId} onReply={setReplyingTo} onDelete={handleDelete} onEdit={handleEdit}/>);
            })}
            <div ref={endRef}/>
          </div>
          {replyingTo&&(
            <div style={{padding:".5rem 1rem",background:"#eff6ff",borderTop:"1px solid #dbeafe",display:"flex",alignItems:"center",gap:".625rem"}}>
              <div style={{width:3,borderRadius:"9999px",background:"#3b82f6",flexShrink:0,alignSelf:"stretch",minHeight:28}}/>
              <div style={{flex:1,minWidth:0}}><p style={{fontSize:".7rem",fontWeight:700,color:"#2563eb",margin:"0 0 1px"}}>Replying to {replyingTo.sender}</p><p style={{fontSize:".75rem",color:"#6b7280",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{replyingTo.text}</p></div>
              <button onClick={()=>setReplyingTo(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:"2px",display:"flex",alignItems:"center",flexShrink:0}}><span className="ms" style={{fontSize:"1.125rem"}}>close</span></button>
            </div>
          )}
          <div style={{padding:".75rem 1rem",borderTop:"1px solid #f3f4f6",display:"flex",alignItems:"flex-end",gap:".625rem",background:"#fff"}}>
            <textarea ref={inputRef} value={input} onChange={e=>{setInput(e.target.value);e.target.style.height="auto";e.target.style.height=Math.min(e.target.scrollHeight,100)+"px";}} onKeyDown={handleKey} placeholder={`Message ${peer.username}…`} rows={1}
              style={{flex:1,border:"1.5px solid #e5e7eb",borderRadius:"1.125rem",padding:".5rem .9rem",fontSize:".875rem",fontFamily:"inherit",resize:"none",outline:"none",lineHeight:1.5,maxHeight:100,minHeight:38,background:"#f9fafb",color:"#111827",transition:"border-color .15s"}}
              onFocus={e=>e.target.style.borderColor="#93c5fd"} onBlur={e=>e.target.style.borderColor="#e5e7eb"}/>
            <button onClick={sendMessage} disabled={!input.trim()||sending}
              style={{width:38,height:38,borderRadius:"11px",border:"none",cursor:!input.trim()||sending?"not-allowed":"pointer",background:!input.trim()||sending?"#f3f4f6":"#3b82f6",color:!input.trim()||sending?"#9ca3af":"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:!input.trim()||sending?"none":"0 4px 12px rgba(59,130,246,.35)",transition:"all .15s"}}>
              {sending?<div style={{width:14,height:14,border:"2px solid #9ca3af",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 1s linear infinite"}}/>:<span className="ms" style={{fontSize:"1.1rem"}}>send</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN HOSPITAL ADMIN DASHBOARD
// ════════════════════════════════════════════════════════════════════════════
const HospitalAdminDashboard = () => {
  const navigate        = useNavigate();
  const adminHospitalId = localStorage.getItem("adminHospitalId");
  const adminUserId     = localStorage.getItem("userId");
  const adminUsername   = localStorage.getItem("username") || "admin";

  const withActor = (body = {}) => ({ ...body, _actorId: adminUserId, _actorName: adminUsername, hospitalId: adminHospitalId });

  const handleLogout = async () => {
    const loginTime  = parseInt(localStorage.getItem("loginTime") || "0");
    const activeTime = loginTime ? Math.floor((Date.now() - loginTime) / 1000) : null;
    await fetch(`${API}/logout`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: adminUserId, username: adminUsername, activeTime }) }).catch(() => {});
    localStorage.clear();
    navigate("/signin");
  };

  const [page,                 setPage]                = useState("overview");
  const [hospitalName,         setHospitalName]         = useState("Hospital");
  const [adminUsernameDisplay, setAdminUsernameDisplay] = useState(adminUsername);

  const [subAdmins,          setSubAdmins]          = useState([]);
  const [users,              setUsers]              = useState([]);
  const [patients,           setPatients]           = useState([]);
  const [patientAssignments, setPatientAssignments] = useState([]);
  const [userAssignments,    setUserAssignments]    = useState([]);
  const [devices,            setDevices]            = useState([]);
  const [deviceTotal,        setDeviceTotal]        = useState(0);
  const [devicePage,         setDevicePage]         = useState(1);
  const [deviceSearch,       setDeviceSearch]       = useState("");
  const [deviceLoading,      setDeviceLoading]      = useState(false);
  const [chatUnread,         setChatUnread]         = useState(0);
  const devicesPerPage = 20;

  const [subAdminSearch, setSubAdminSearch] = useState("");
  const [userSearch,     setUserSearch]     = useState("");
  const [patientSearch,  setPatientSearch]  = useState("");

  const [selectedPatient,      setSelectedPatient]      = useState("");
  const [selectedSubAdmin,     setSelectedSubAdmin]     = useState("");
  const [selectedUser,         setSelectedUser]         = useState("");
  const [selectedSubAdminUser, setSelectedSubAdminUser] = useState("");

  const [addSubAdminModal,  setAddSubAdminModal]  = useState(false);
  const [viewSubAdminModal, setViewSubAdminModal] = useState(null);
  const [editSubAdminModal, setEditSubAdminModal] = useState(null);
  const [addUserModal,      setAddUserModal]      = useState(false);
  const [viewUserModal,     setViewUserModal]     = useState(null);
  const [editUserModal,     setEditUserModal]     = useState(null);
  const [newUser,           setNewUser]           = useState({ username: "", email: "", password: "" });
  const [addPatientModal,   setAddPatientModal]   = useState(false);
  const [viewPatientModal,  setViewPatientModal]  = useState(null);
  const [editPatientModal,  setEditPatientModal]  = useState(null);
  const [linkModalDevice,   setLinkModalDevice]   = useState(null);
  const [linkPatientId,     setLinkPatientId]     = useState("");

  const [editSAForm, setEditSAForm] = useState({ username: "", email: "", password: "" });
  const [editUForm,  setEditUForm]  = useState({ username: "", email: "", password: "" });

  const chatPollRef = useRef(null);

  const fetchHospitalInfo = async () => {
    try { const res = await fetch(`${API}/hospital-info?hospitalId=${adminHospitalId}`); const d = await res.json(); setHospitalName(d.hospital_name); setAdminUsernameDisplay(d.admin_username || adminUsername); } catch {}
  };
  const fetchSubAdmins = async () => { try { const r = await fetch(`${API}/subadmins?hospitalId=${adminHospitalId}`); const d = await r.json(); setSubAdmins(Array.isArray(d) ? d : []); } catch { setSubAdmins([]); } };
  const fetchUsers     = async () => {
    if (!adminHospitalId) { setUsers([]); return; }
    try {
      const r = await fetch(`${API}/users?hospitalId=${adminHospitalId}`);
      if (!r.ok) { setUsers([]); return; }
      const d = await r.json();
      setUsers(Array.isArray(d) ? d : []);
    } catch { setUsers([]); }
  };
  const fetchPatients  = async () => { try { const r = await fetch(`${API}/patients?hospitalId=${adminHospitalId}`);  const d = await r.json(); setPatients(Array.isArray(d) ? d : []);  } catch { setPatients([]);  } };
  const fetchPatientAssignments = async () => { try { const r = await fetch(`${API}/patient-subadmin?hospitalId=${adminHospitalId}`); const d = await r.json(); setPatientAssignments(Array.isArray(d) ? d : []); } catch { setPatientAssignments([]); } };
  const fetchUserAssignments    = async () => { try { const r = await fetch(`${API}/user-subadmin?hospitalId=${adminHospitalId}`);    const d = await r.json(); setUserAssignments(Array.isArray(d) ? d : []);    } catch { setUserAssignments([]);    } };

  const fetchDevices = useCallback(async (pg = 1, search = "") => {
    setDeviceLoading(true);
    try { const params = new URLSearchParams({ hospitalId: adminHospitalId, page: pg, limit: devicesPerPage, search }); const r = await fetch(`${API}/admin/devices?${params}`); const d = await r.json(); setDevices(d.devices || []); setDeviceTotal(d.total || 0); } catch {}
    finally { setDeviceLoading(false); }
  }, [adminHospitalId]);

  const fetchChatUnread = useCallback(async () => {
    try { const r = await fetch(`${API}/chat/unread?userId=${adminUserId}`); if (!r.ok) return; const data = await r.json(); const total = (Array.isArray(data) ? data : []).reduce((a, b) => a + (+b.count), 0); setChatUnread(total); } catch {}
  }, [adminUserId]);

  useEffect(() => {
    fetchHospitalInfo(); fetchSubAdmins(); fetchUsers(); fetchPatients(); fetchPatientAssignments(); fetchUserAssignments(); fetchDevices(); fetchChatUnread();
    chatPollRef.current = setInterval(fetchChatUnread, 8000);
    return () => clearInterval(chatPollRef.current);
  }, []);
  useEffect(() => { fetchDevices(devicePage, deviceSearch); }, [devicePage, deviceSearch]);

  const toggleSubAdminActivation = async (sa) => {
    const newState = !sa.is_active;
    if (!window.confirm(`${newState ? "Activate" : "Deactivate"} "${sa.username}"?`)) return;
    const res = await fetch(`${API}/subadmins/${sa.id}/toggle-activation`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ is_active: newState })) });
    if (!res.ok) { const d = await res.json(); alert(d.error || "Failed"); return; }
    fetchSubAdmins();
  };

  const toggleUserActivation = async (u) => {
    const newState = !u.is_active;
    if (!window.confirm(`${newState ? "Activate" : "Deactivate"} "${u.username}"?`)) return;
    const res = await fetch(`${API}/users/${u.id}/toggle-activation`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ is_active: newState })) });
    if (!res.ok) { const d = await res.json(); alert(d.error || "Failed"); return; }
    fetchUsers();
  };

  const handleDeleteSubAdmin = async (id) => { if (!window.confirm("Delete?")) return; await fetch(`${API}/subadmins/${id}`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({})) }); fetchSubAdmins(); };
  const handleDeleteUser     = async (id) => { if (!window.confirm("Delete?")) return; await fetch(`${API}/users/${id}`,    { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({})) }); fetchUsers(); };
  const handleDeletePatient  = async (id) => { if (!window.confirm("Delete?")) return; await fetch(`${API}/patients/${id}`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({})) }); fetchPatients(); };

  const openEditSubAdmin = (sa) => { setEditSAForm({ username: sa.username, email: sa.email, password: "" }); setEditSubAdminModal(sa); };
  const handleEditSubAdmin = async () => {
    const { username, email, password } = editSAForm;
    if (!username || !email) { alert("Username and email are required"); return; }
    const res = await fetch(`${API}/subadmins/${editSubAdminModal.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ username, email, password })) });
    const d = await res.json();
    if (!res.ok) { alert(d.message || "Failed to update"); return; }
    fetchSubAdmins(); setEditSubAdminModal(null);
  };

  const openEditUser = (u) => { setEditUForm({ username: u.username, email: u.email, password: "" }); setEditUserModal(u); };
  const handleEditUser = async () => {
    const { username, email, password } = editUForm;
    if (!username || !email) { alert("Username and email are required"); return; }
    const res = await fetch(`${API}/users/${editUserModal.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ username, email, password })) });
    const d = await res.json();
    if (!res.ok) { alert(d.message || "Failed to update"); return; }
    fetchUsers(); setEditUserModal(null);
  };

  const handleAssignPatient = async () => {
    if (!selectedPatient || !selectedSubAdmin) return;
    const res = await fetch(`${API}/patient-subadmin`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ patient_id: selectedPatient, sous_admin_id: selectedSubAdmin })) });
    const d = await res.json(); if (!res.ok) { alert(d.error); return; }
    setSelectedPatient(""); setSelectedSubAdmin(""); fetchPatientAssignments();
  };
  const handleAssignUser = async () => {
    if (!selectedUser || !selectedSubAdminUser) return;
    const res = await fetch(`${API}/user-subadmin`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ user_id: selectedUser, sous_admin_id: selectedSubAdminUser })) });
    const d = await res.json(); if (!res.ok) { alert(d.error); return; }
    setSelectedUser(""); setSelectedSubAdminUser(""); fetchUserAssignments();
  };
  const handleRemovePatientAssignment = async (id) => { if (!window.confirm("Remove?")) return; await fetch(`${API}/patient-subadmin/${id}`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({})) }); fetchPatientAssignments(); };
  const handleRemoveUserAssignment    = async (id) => { if (!window.confirm("Remove?")) return; await fetch(`${API}/user-subadmin/${id}`,    { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({})) }); fetchUserAssignments(); };

  const handleLinkDevice = async () => {
    if (!linkModalDevice) return;
    const res = await fetch(`${API}/admin/devices/${linkModalDevice.id}/link`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ patient_id: linkPatientId ? Number(linkPatientId) : null })) });
    const d = await res.json(); if (!res.ok) { alert(d.error); return; }
    setLinkModalDevice(null); setLinkPatientId(""); fetchDevices(devicePage, deviceSearch);
  };

  const filtSA = subAdmins.filter(a => a.username.toLowerCase().includes(subAdminSearch.toLowerCase()));
  const filtU  = users.filter(u => u.username.toLowerCase().includes(userSearch.toLowerCase()));
  const filtP  = patients.filter(p => p.name.toLowerCase().includes(patientSearch.toLowerCase()));

  const notActivatedSA = subAdmins.filter(a => !a.is_active).length;
  const notActivatedU  = users.filter(u => !u.is_active).length;
  const totalDevPages  = Math.ceil(deviceTotal / devicesPerPage);

  // Only show 5 nav items in bottom bar (most important), rest accessible via overflow
  const BOTTOM_NAV = [
    { id: "overview",    icon: "dashboard",          label: "Overview"   },
    { id: "patients",    icon: "folder_shared",      label: "Patients"   },
    { id: "subadmins",   icon: "supervisor_account", label: "Doctors"    },
    { id: "devices",     icon: "devices",            label: "Devices"    },
    { id: "chat",        icon: "chat_bubble",        label: "Chat"       },
  ];

  const navWithBadges = NAV.map(n => {
    if (n.id === "subadmins") return { ...n, badge: notActivatedSA };
    if (n.id === "users")     return { ...n, badge: notActivatedU };
    if (n.id === "chat")      return { ...n, badge: chatUnread };
    return n;
  });

  const bottomNavWithBadges = BOTTOM_NAV.map(n => {
    const found = navWithBadges.find(x => x.id === n.id);
    return found ? { ...n, badge: found.badge } : n;
  });

  const EditUserModal = ({ title, accentBg, accentColor, form, setForm, onSave, onClose }) => (
    <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
      <div className="card modal-box" style={{ width: "100%", maxWidth: 480, padding: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: "1rem" }}>close</span></button>
        </div>
        <div style={{ background: accentBg, border: `1px solid ${accentColor}33`, borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: "1.25rem", display: "flex", alignItems: "flex-start", gap: ".75rem" }}>
          <span className="ms" style={{ color: accentColor, fontSize: "1.125rem", flexShrink: 0, marginTop: "1px" }}>info</span>
          <p style={{ fontSize: ".8125rem", color: accentColor, margin: 0, lineHeight: 1.5 }}>Leave <strong>password</strong> blank to keep the current one.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
          {[["Username", "username", "text"], ["Email", "email", "email"]].map(([l, k, t]) => (
            <div key={k}>
              <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>{l}</label>
              <input type={t} className="inp" value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
            </div>
          ))}
          <div>
            <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>New Password <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
            <input type="password" className="inp" placeholder="Leave blank to keep current" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
          </div>
          <div style={{ display: "flex", gap: ".75rem", marginTop: ".25rem" }}>
            <button onClick={onSave} className="bblu" style={{ flex: 1, justifyContent: "center" }}>Save Changes</button>
            <button onClick={onClose} className="bgry" style={{ flex: 1 }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0&display=swap');
        .ms { font-family:'Material Symbols Outlined'; font-style:normal; font-weight:normal; line-height:1; display:inline-block; white-space:nowrap; direction:ltr; -webkit-font-smoothing:antialiased; }
        ::-webkit-scrollbar { width:5px; height:5px } ::-webkit-scrollbar-track { background:transparent } ::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:20px }
        .card { background:#fff; border-radius:1.5rem; box-shadow:0 4px 20px -2px rgba(0,0,0,.06); }
        .pill { border:none; cursor:pointer; font-family:inherit; font-size:.8125rem; font-weight:700; border-radius:9999px; padding:.45rem 1rem; display:inline-flex; align-items:center; gap:.375rem; transition:all .15s; white-space:nowrap; }
        .inp { border:1.5px solid #e5e7eb; border-radius:.875rem; padding:.75rem 1rem; font-size:.875rem; outline:none; width:100%; box-sizing:border-box; font-family:inherit; transition:border-color .15s; background:#fff; }
        .inp:focus { border-color:#93c5fd; box-shadow:0 0 0 3px rgba(59,130,246,.1); }
        .bblu { background:#3b82f6; color:#fff; border:none; font-family:inherit; font-weight:700; border-radius:.875rem; padding:.75rem 1.5rem; cursor:pointer; font-size:.9375rem; display:inline-flex; align-items:center; gap:.5rem; transition:background .15s; }
        .bblu:hover { background:#2563eb; }
        .bgry { background:#f3f4f6; color:#374151; border:none; font-family:inherit; font-weight:600; border-radius:.875rem; padding:.75rem 1.5rem; cursor:pointer; font-size:.9375rem; transition:background .15s; }
        .bgry:hover { background:#e5e7eb; }
        .nav-item { display:flex; align-items:center; gap:.75rem; padding:.75rem 1.25rem; border-radius:1rem; cursor:pointer; font-size:.875rem; font-weight:600; color:#6b7280; transition:all .15s; border:none; background:none; font-family:inherit; width:100%; text-align:left; }
        .nav-item:hover { background:#f9fafb; color:#111827; }
        .nav-item.active { background:#eff6ff; color:#2563eb; }
        .trow:hover { background:#f9fafb; }
        .hovcard { transition:box-shadow .2s; } .hovcard:hover { box-shadow:0 8px 28px -4px rgba(0,0,0,.12); }
        .sect-title { font-size:1.625rem; font-weight:800; color:#111827; letter-spacing:-.02em; margin:0 0 .25rem; }
        .sect-sub { font-size:.875rem; color:#6b7280; margin:0 0 1.75rem; }
        .badge { display:inline-flex; align-items:center; gap:.3rem; font-size:.7rem; font-weight:700; padding:2px 9px; border-radius:9999px; }
        .toggle-btn { border:none; cursor:pointer; font-family:inherit; font-size:.7rem; font-weight:700; border-radius:9999px; padding:.3rem .75rem; display:inline-flex; align-items:center; gap:.3rem; transition:all .15s; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }
        @keyframes pulse { 0%,100%{opacity:1}50%{opacity:.4} }
        @keyframes spin { to { transform:rotate(360deg) } }
        .fade-in { animation:fadeIn .25s ease both }
        .pls { animation:pulse 2s infinite }
        select, textarea, input { font-family:'Plus Jakarta Sans', sans-serif; }

        /* ── Modal sizing ── */
        .modal-box {
          max-height: 90dvh;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        /* ── Chat grid desktop ── */
        .chat-grid {
          grid-template-columns: 300px 1fr !important;
        }
        .chat-contacts { display: flex !important; }
        .chat-empty { display: flex !important; }

        /* ── Sidebar: desktop only ── */
        .sidebar {
          width: 256px; flex-shrink: 0; background: #fff;
          border-right: 1px solid #f0f0f0;
          display: flex; flex-direction: column;
          padding: 1.5rem 1rem;
          position: sticky; top: 0; height: 100vh; overflow: hidden;
        }

        /* ── Desktop header ── */
        .desktop-header { display: flex; }

        /* ── Mobile header: hidden on desktop ── */
        .mobile-header { display: none; }

        /* ── Bottom nav: hidden on desktop ── */
        .bottom-nav { display: none; }

        /* ── Main padding ── */
        .main-pad { padding: 2rem; max-width: 1200px; margin: 0 auto; }

        /* ── Assignments grid desktop ── */
        .assign-grid { grid-template-columns: 1fr 1fr auto !important; }

        /* ════ MOBILE ════ */
        @media (max-width: 768px) {
          /* hide sidebar */
          .sidebar { display: none !important; }

          /* show mobile header, hide desktop header */
          .desktop-header { display: none !important; }
          .mobile-header { display: flex !important; }

          /* show bottom nav */
          .bottom-nav {
            display: block;
            position: fixed; bottom: 0; left: 0; right: 0;
            background: #fff;
            border-top: 1px solid #f0f0f0;
            z-index: 50;
            padding-bottom: env(safe-area-inset-bottom);
          }

          /* extra bottom padding so content clears the bottom nav */
          .main-pad {
            padding: 1rem;
            padding-bottom: calc(64px + env(safe-area-inset-bottom) + 1rem);
          }

          /* modals slide up from bottom on mobile */
          .modal-overlay {
            align-items: flex-end !important;
            padding: 0 !important;
          }
          .modal-box {
            border-radius: 1.5rem 1.5rem 0 0 !important;
            max-height: 92dvh;
          }

          /* section titles smaller */
          .sect-title { font-size: 1.2rem; }
          .sect-sub   { font-size: .78rem; margin-bottom: 1.1rem; }

          /* stat cards: 2 columns */
          .stat-grid { grid-template-columns: repeat(2, 1fr) !important; gap: .625rem !important; }

          /* all cards grids: single column */
          .cards-grid { grid-template-columns: 1fr !important; }

          /* assignments: stack vertically */
          .assign-grid { grid-template-columns: 1fr !important; }

          /* chat: full screen, contacts list hides when peer selected */
          .chat-grid {
            grid-template-columns: 1fr !important;
            min-height: calc(100dvh - 180px) !important;
          }
          .chat-contacts-hidden { display: none !important; }
          .chat-empty { min-height: 300px !important; }

          /* hide edit pill on mobile (too narrow) */
          .edit-pill { display: none !important; }
        }
      `}</style>

      <div style={{ display: "flex", minHeight: "100vh" }}>

        {/* ══ SIDEBAR (desktop) ══ */}
        <aside className="sidebar">
          <div style={{ display: "flex", alignItems: "center", gap: ".75rem", padding: "0 .25rem", marginBottom: "2rem" }}>
            <div style={{ width: 38, height: 38, borderRadius: "12px", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span className="ms" style={{ color: "#fff", fontSize: "1.125rem" }}>local_hospital</span>
            </div>
            <div>
              <p style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", margin: 0, letterSpacing: "-.02em" }}>CareTech</p>
              <p style={{ fontSize: ".65rem", color: "#9ca3af", margin: 0, textTransform: "uppercase", letterSpacing: ".06em" }}>Admin Portal</p>
            </div>
          </div>

          <div style={{ background: "#f9fafb", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: "1.5rem" }}>
            <p style={{ fontWeight: 700, fontSize: ".875rem", color: "#111827", margin: "0 0 .125rem" }}>{hospitalName}</p>
            <p style={{ fontSize: ".7rem", color: "#9ca3af", margin: 0 }}>@{adminUsernameDisplay}</p>
          </div>

          <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: ".25rem" }}>
            {navWithBadges.map(n => (
              <button key={n.id} onClick={() => setPage(n.id)} className={`nav-item ${page === n.id ? "active" : ""}`}>
                <span className="ms" style={{ fontSize: "1.125rem" }}>{n.icon}</span>
                {n.label}
                {n.badge > 0 && (
                  <span style={{ marginLeft: "auto", background: n.id === "chat" ? "#8b5cf6" : "#f59e0b", color: "#fff", fontSize: ".6rem", fontWeight: 700, padding: "2px 7px", borderRadius: "9999px" }}>{n.badge}</span>
                )}
              </button>
            ))}
          </nav>

          <button onClick={handleLogout} className="nav-item" style={{ color: "#ef4444", marginTop: "1rem" }}>
            <span className="ms" style={{ fontSize: "1.125rem" }}>logout</span>Log Out
          </button>
        </aside>

        {/* ══ MAIN ══ */}
        <main style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>

          {/* ── Desktop header ── */}
          <header className="desktop-header" style={{ background: "#fff", borderBottom: "1px solid #f0f0f0", padding: "1rem 2rem", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 40 }}>
            <h1 style={{ fontWeight: 800, fontSize: "1.125rem", color: "#111827", margin: 0, letterSpacing: "-.02em" }}>
              {navWithBadges.find(n => n.id === page)?.label || "Dashboard"}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e" }} className="pls" />
                <span style={{ fontSize: ".75rem", color: "#6b7280" }}>Live</span>
              </div>
              {(notActivatedSA + notActivatedU) > 0 && (
                <span style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "9999px", padding: ".35rem .9rem", color: "#b45309", fontSize: ".75rem", fontWeight: 700 }}>
                  {notActivatedSA + notActivatedU} not activated
                </span>
              )}
              {chatUnread > 0 && (
                <button onClick={() => setPage("chat")} style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "9999px", padding: ".35rem .9rem", color: "#6d28d9", fontSize: ".75rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: ".4rem" }}>
                  <span className="ms" style={{ fontSize: ".9rem" }}>chat_bubble</span>{chatUnread} unread
                </button>
              )}
            </div>
          </header>

          {/* ── Mobile header ── */}
          <header className="mobile-header" style={{ background: "#fff", borderBottom: "1px solid #f0f0f0", padding: ".75rem 1rem", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".625rem" }}>
              <div style={{ width: 32, height: 32, borderRadius: "10px", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span className="ms" style={{ color: "#fff", fontSize: "1rem" }}>local_hospital</span>
              </div>
              <div>
                <p style={{ fontWeight: 800, fontSize: ".9rem", color: "#111827", margin: 0 }}>{hospitalName}</p>
                <p style={{ fontSize: ".6rem", color: "#9ca3af", margin: 0 }}>@{adminUsernameDisplay}</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: ".625rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: ".375rem" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} className="pls" />
                <span style={{ fontSize: ".7rem", color: "#6b7280" }}>Live</span>
              </div>
              {(notActivatedSA + notActivatedU) > 0 && (
                <span style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "9999px", padding: ".25rem .625rem", color: "#b45309", fontSize: ".7rem", fontWeight: 700 }}>
                  {notActivatedSA + notActivatedU} pending
                </span>
              )}
              {chatUnread > 0 && (
                <button onClick={() => setPage("chat")} style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "9999px", padding: ".25rem .625rem", color: "#6d28d9", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: ".3rem" }}>
                  <span className="ms" style={{ fontSize: ".8rem" }}>chat_bubble</span>{chatUnread}
                </button>
              )}
              <button onClick={handleLogout} style={{ width: 32, height: 32, borderRadius: "10px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}>
                <span className="ms" style={{ fontSize: "1rem" }}>logout</span>
              </button>
            </div>
          </header>

          <div className="main-pad fade-in">

            {/* ════════ OVERVIEW ════════ */}
            {page === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <div>
                  <h2 className="sect-title">Welcome, {adminUsernameDisplay}</h2>
                  <p className="sect-sub">Hospital administration overview for {hospitalName}.</p>
                </div>
                <div className="stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "1rem" }}>
                  {[
                    { label: "Sub-Admins",         val: subAdmins.filter(s => s.is_active).length, icon: "supervisor_account", bg: "#eff6ff", c: "#2563eb" },
                    { label: "Not Activated SA",    val: notActivatedSA,                            icon: "person_off",         bg: "#fffbeb", c: "#d97706" },
                    { label: "Medical Staff",       val: users.filter(u => u.is_active).length,     icon: "people",             bg: "#f0fdf4", c: "#16a34a" },
                    { label: "Not Activated Staff", val: notActivatedU,                             icon: "pending_actions",    bg: "#fffbeb", c: "#d97706" },
                    { label: "Patients",            val: patients.length,                           icon: "folder_shared",      bg: "#f5f3ff", c: "#7c3aed" },
                    { label: "Devices",             val: deviceTotal,                               icon: "devices",            bg: "#fef3c7", c: "#d97706" },
                  ].map(s => (
                    <div key={s.label} className="card hovcard" style={{ padding: "1.25rem" }}>
                      <div style={{ width: 40, height: 40, borderRadius: "12px", background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: ".875rem" }}>
                        <span className="ms" style={{ color: s.c, fontSize: "1.125rem" }}>{s.icon}</span>
                      </div>
                      <p style={{ fontSize: "1.875rem", fontWeight: 800, color: "#111827", margin: "0 0 .125rem" }}>{s.val}</p>
                      <p style={{ fontSize: ".75rem", color: "#9ca3af", fontWeight: 600, margin: 0, textTransform: "uppercase", letterSpacing: ".04em" }}>{s.label}</p>
                    </div>
                  ))}
                </div>

                <div onClick={() => setPage("chat")} className="card hovcard" style={{ padding: "1.25rem", cursor: "pointer", background: "linear-gradient(135deg,#f5f3ff,#ede9fe)", border: "1px solid #ddd6fe" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                    <div style={{ width: 44, height: 44, borderRadius: "14px", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span className="ms" style={{ color: "#fff", fontSize: "1.25rem" }}>chat_bubble</span>
                    </div>
                    <div>
                      <p style={{ fontWeight: 700, color: "#5b21b6", margin: 0 }}>Hospital Chat</p>
                      <p style={{ fontSize: ".75rem", color: "#6b7280", margin: 0 }}>Message colleagues instantly{chatUnread > 0 && <span style={{ background: "#ef4444", color: "#fff", fontSize: ".6rem", fontWeight: 700, padding: "1px 6px", borderRadius: "9999px", marginLeft: ".375rem" }}>{chatUnread} unread</span>}</p>
                    </div>
                    <span className="ms" style={{ marginLeft: "auto", color: "#8b5cf6" }}>arrow_forward</span>
                  </div>
                </div>

                {/* Quick nav cards for mobile */}
                <div className="cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" }}>
                  {[
                    { id: "subadmins",   icon: "supervisor_account", label: "Manage Sub-Admins",   bg: "#eff6ff",  c: "#2563eb",  badge: notActivatedSA },
                    { id: "users",       icon: "people",             label: "Manage Medical Staff", bg: "#f0fdf4",  c: "#16a34a",  badge: notActivatedU  },
                    { id: "patients",    icon: "folder_shared",      label: "Manage Patients",      bg: "#f5f3ff",  c: "#7c3aed",  badge: 0              },
                    { id: "assignments", icon: "assignment_ind",     label: "Assignments",          bg: "#fef3c7",  c: "#d97706",  badge: 0              },
                  ].map(n => (
                    <div key={n.id} onClick={() => setPage(n.id)} className="card hovcard" style={{ padding: "1rem 1.25rem", cursor: "pointer", display: "flex", alignItems: "center", gap: ".875rem" }}>
                      <div style={{ width: 40, height: 40, borderRadius: "12px", background: n.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span className="ms" style={{ color: n.c, fontSize: "1.125rem" }}>{n.icon}</span>
                      </div>
                      <span style={{ fontWeight: 700, color: "#111827", fontSize: ".9375rem", flex: 1 }}>{n.label}</span>
                      {n.badge > 0 && <span style={{ background: "#f59e0b", color: "#fff", fontSize: ".6rem", fontWeight: 700, padding: "2px 7px", borderRadius: "9999px" }}>{n.badge}</span>}
                      <span className="ms" style={{ color: "#9ca3af", fontSize: "1rem" }}>arrow_forward_ios</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ════════ SUB-ADMINS ════════ */}
            {page === "subadmins" && (
              <div>
                <h2 className="sect-title">Sub-Admins</h2>
                <p className="sect-sub">Manage doctors. Activate or deactivate their access.</p>
                <div style={{ display: "flex", gap: ".75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                  <div style={{ position: "relative", flex: 1, maxWidth: 340 }}>
                    <span className="ms" style={{ position: "absolute", left: ".875rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1.125rem" }}>search</span>
                    <input className="inp" style={{ paddingLeft: "2.75rem" }} placeholder="Search sub-admins…" value={subAdminSearch} onChange={e => setSubAdminSearch(e.target.value)} />
                  </div>
                  <button onClick={() => setAddSubAdminModal(true)} className="bblu" style={{ boxShadow: "0 4px 14px rgba(59,130,246,.3)" }}>
                    <span className="ms" style={{ fontSize: "1.125rem" }}>add_circle</span>Add
                  </button>
                </div>
                <div className="cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" }}>
                  {filtSA.length === 0 && <p style={{ color: "#9ca3af" }}>No sub-admins found.</p>}
                  {filtSA.map(sa => (
                    <div key={sa.id} className="card hovcard" style={{ padding: "1.25rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: ".875rem", marginBottom: "1rem" }}>
                        <div style={{ width: 44, height: 44, borderRadius: "14px", background: sa.is_active ? "#eff6ff" : "#f3f4f6", color: sa.is_active ? "#2563eb" : "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem", flexShrink: 0 }}>
                          <span className="ms">supervisor_account</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: 700, color: "#111827", margin: 0 }}>{sa.username}</p>
                          <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: 0 }}>{sa.email}</p>
                        </div>
                        <span className="badge" style={{ background: sa.is_active ? "#f0fdf4" : "#fef2f2", color: sa.is_active ? "#166534" : "#991b1b" }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: sa.is_active ? "#22c55e" : "#ef4444" }} />
                          {sa.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                        <button onClick={() => setViewSubAdminModal(sa)} className="pill" style={{ background: "#eff6ff", color: "#2563eb" }}>
                          <span className="ms" style={{ fontSize: ".9rem" }}>visibility</span>View
                        </button>
                        <button onClick={() => openEditSubAdmin(sa)} className="pill" style={{ background: "#f0fdf4", color: "#16a34a" }}>
                          <span className="ms" style={{ fontSize: ".9rem" }}>edit</span>Edit
                        </button>
                        <button onClick={() => toggleSubAdminActivation(sa)} className="toggle-btn" style={{ background: sa.is_active ? "#fef2f2" : "#f0fdf4", color: sa.is_active ? "#991b1b" : "#166534" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>{sa.is_active ? "block" : "check_circle"}</span>
                          {sa.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button onClick={() => handleDeleteSubAdmin(sa.id)} style={{ width: 36, height: 36, borderRadius: "10px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>delete</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ════════ MEDICAL STAFF ════════ */}
            {page === "users" && (
              <div>
                <h2 className="sect-title">Medical Staff</h2>
                <p className="sect-sub">Manage hospital staff accounts. Activate or deactivate their access.</p>
                <div style={{ display: "flex", gap: ".75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                  <div style={{ position: "relative", flex: 1, maxWidth: 340 }}>
                    <span className="ms" style={{ position: "absolute", left: ".875rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1.125rem" }}>search</span>
                    <input className="inp" style={{ paddingLeft: "2.75rem" }} placeholder="Search medical staff…" value={userSearch} onChange={e => setUserSearch(e.target.value)} />
                  </div>
                  <button onClick={() => setAddUserModal(true)} className="bblu" style={{ boxShadow: "0 4px 14px rgba(59,130,246,.3)" }}>
                    <span className="ms" style={{ fontSize: "1.125rem" }}>add_circle</span>Add
                  </button>
                </div>
                <div className="cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" }}>
                  {filtU.length === 0 && <p style={{ color: "#9ca3af" }}>No medical staff found.</p>}
                  {filtU.map(u => (
                    <div key={u.id} className="card hovcard" style={{ padding: "1.25rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: ".875rem", marginBottom: "1rem" }}>
                        <div style={{ width: 44, height: 44, borderRadius: "14px", background: u.is_active ? "#f0fdf4" : "#f3f4f6", color: u.is_active ? "#16a34a" : "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem", flexShrink: 0 }}>
                          <span className="ms">people</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: 700, color: "#111827", margin: 0 }}>{u.username}</p>
                          <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: 0 }}>{u.email}</p>
                        </div>
                        <span className="badge" style={{ background: u.is_active ? "#f0fdf4" : "#fef2f2", color: u.is_active ? "#166534" : "#991b1b" }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: u.is_active ? "#22c55e" : "#ef4444" }} />
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                        <button onClick={() => setViewUserModal(u)} className="pill" style={{ background: "#f0fdf4", color: "#16a34a" }}>
                          <span className="ms" style={{ fontSize: ".9rem" }}>visibility</span>View
                        </button>
                        <button onClick={() => openEditUser(u)} className="pill" style={{ background: "#eff6ff", color: "#2563eb" }}>
                          <span className="ms" style={{ fontSize: ".9rem" }}>edit</span>Edit
                        </button>
                        <button onClick={() => toggleUserActivation(u)} className="toggle-btn" style={{ background: u.is_active ? "#fef2f2" : "#f0fdf4", color: u.is_active ? "#991b1b" : "#166634" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>{u.is_active ? "block" : "check_circle"}</span>
                          {u.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button onClick={() => handleDeleteUser(u.id)} style={{ width: 36, height: 36, borderRadius: "10px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>delete</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ════════ PATIENTS ════════ */}
            {page === "patients" && (
              <div>
                <h2 className="sect-title">Patients</h2>
                <p className="sect-sub">Manage patient records.</p>
                <div style={{ display: "flex", gap: ".75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                  <div style={{ position: "relative", flex: 1, maxWidth: 340 }}>
                    <span className="ms" style={{ position: "absolute", left: ".875rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1.125rem" }}>search</span>
                    <input className="inp" style={{ paddingLeft: "2.75rem" }} placeholder="Search patients…" value={patientSearch} onChange={e => setPatientSearch(e.target.value)} />
                  </div>
                  <button onClick={() => setAddPatientModal(true)} className="bblu" style={{ boxShadow: "0 4px 14px rgba(59,130,246,.3)" }}>
                    <span className="ms" style={{ fontSize: "1.125rem" }}>add_circle</span>Add
                  </button>
                </div>
                <div className="cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" }}>
                  {filtP.length === 0 && <p style={{ color: "#9ca3af" }}>No patients found.</p>}
                  {filtP.map(p => (
                    <div key={p.id} className="card hovcard" style={{ padding: "1.25rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: ".875rem", marginBottom: "1rem" }}>
                        <div style={{ width: 44, height: 44, borderRadius: "14px", background: "#f5f3ff", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "1.125rem", flexShrink: 0 }}>{p.name.charAt(0)}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: 700, color: "#111827", margin: 0 }}>{p.name}</p>
                          <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: 0 }}>DOB: {fmtDob(p.dob)}</p>
                        </div>
                      </div>
                      {p.email && <p style={{ fontSize: ".8125rem", color: "#6b7280", margin: "0 0 .875rem", display: "flex", alignItems: "center", gap: ".375rem" }}><span className="ms" style={{ fontSize: ".875rem" }}>mail</span>{p.email}</p>}
                      <div style={{ display: "flex", gap: ".5rem" }}>
                        <button onClick={() => setViewPatientModal(p)} className="pill" style={{ flex: 1, background: "#f5f3ff", color: "#7c3aed", justifyContent: "center" }}><span className="ms" style={{ fontSize: ".9rem" }}>visibility</span>View</button>
                        <button onClick={() => setEditPatientModal(p)} style={{ width: 36, height: 36, borderRadius: "10px", background: "#f9fafb", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: ".875rem" }}>edit</span></button>
                        <button onClick={() => handleDeletePatient(p.id)} style={{ width: 36, height: 36, borderRadius: "10px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}><span className="ms" style={{ fontSize: ".875rem" }}>delete</span></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ════════ ASSIGNMENTS ════════ */}
            {page === "assignments" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <div>
                  <h2 className="sect-title">Assignments</h2>
                  <p className="sect-sub">Link patients and medical staff to sub-admins (doctors).</p>
                </div>
                <div className="card" style={{ padding: "1.5rem" }}>
                  <h3 style={{ fontWeight: 700, fontSize: "1rem", color: "#111827", margin: "0 0 1.25rem" }}>Patient → Sub-Admin (Doctor)</h3>
                  <div className="assign-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: ".875rem", marginBottom: "1.25rem", alignItems: "end" }}>
                    <div>
                      <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Patient</label>
                      <select className="inp" value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)}>
                        <option value="">Select patient…</option>
                        {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Doctor</label>
                      <select className="inp" value={selectedSubAdmin} onChange={e => setSelectedSubAdmin(e.target.value)}>
                        <option value="">Select doctor…</option>
                        {subAdmins.filter(s => s.is_active).map(sa => <option key={sa.id} value={sa.id}>{sa.username}</option>)}
                      </select>
                    </div>
                    <button onClick={handleAssignPatient} className="bblu" style={{ whiteSpace: "nowrap" }}><span className="ms" style={{ fontSize: "1rem" }}>link</span>Assign</button>
                  </div>
                  <div className="card" style={{ overflow: "hidden", boxShadow: "none", border: "1px solid #f3f4f6" }}>
                    {patientAssignments.length === 0
                      ? <p style={{ color: "#9ca3af", padding: "1rem 1.25rem", margin: 0 }}>No assignments yet.</p>
                      : patientAssignments.map((a, i) => (
                        <div key={a.id} className="trow" style={{ display: "flex", alignItems: "center", padding: ".875rem 1.25rem", borderBottom: i < patientAssignments.length - 1 ? "1px solid #f3f4f6" : "none", gap: ".5rem" }}>
                          <span style={{ fontWeight: 600, color: "#111827", flex: 1, fontSize: ".875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.patient_name}</span>
                          <span className="ms" style={{ color: "#9ca3af", fontSize: "1rem", flexShrink: 0 }}>arrow_forward</span>
                          <span style={{ color: "#6b7280", flex: 1, fontSize: ".875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.subadmin_name}</span>
                          <button onClick={() => handleRemovePatientAssignment(a.id)} style={{ width: 32, height: 32, borderRadius: "8px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", flexShrink: 0 }}><span className="ms" style={{ fontSize: ".875rem" }}>delete</span></button>
                        </div>
                      ))}
                  </div>
                </div>
                <div className="card" style={{ padding: "1.5rem" }}>
                  <h3 style={{ fontWeight: 700, fontSize: "1rem", color: "#111827", margin: "0 0 1.25rem" }}>Medical Staff → Sub-Admin (Doctor)</h3>
                  <div className="assign-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: ".875rem", marginBottom: "1.25rem", alignItems: "end" }}>
                    <div>
                      <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Medical Staff</label>
                      <select className="inp" value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
                        <option value="">Select staff…</option>
                        {users.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Doctor</label>
                      <select className="inp" value={selectedSubAdminUser} onChange={e => setSelectedSubAdminUser(e.target.value)}>
                        <option value="">Select doctor…</option>
                        {subAdmins.filter(s => s.is_active).map(sa => <option key={sa.id} value={sa.id}>{sa.username}</option>)}
                      </select>
                    </div>
                    <button onClick={handleAssignUser} className="bblu" style={{ whiteSpace: "nowrap" }}><span className="ms" style={{ fontSize: "1rem" }}>link</span>Assign</button>
                  </div>
                  <div className="card" style={{ overflow: "hidden", boxShadow: "none", border: "1px solid #f3f4f6" }}>
                    {userAssignments.length === 0
                      ? <p style={{ color: "#9ca3af", padding: "1rem 1.25rem", margin: 0 }}>No assignments yet.</p>
                      : userAssignments.map((a, i) => (
                        <div key={a.id} className="trow" style={{ display: "flex", alignItems: "center", padding: ".875rem 1.25rem", borderBottom: i < userAssignments.length - 1 ? "1px solid #f3f4f6" : "none", gap: ".5rem" }}>
                          <span style={{ fontWeight: 600, color: "#111827", flex: 1, fontSize: ".875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.medical_stuff_name}</span>
                          <span className="ms" style={{ color: "#9ca3af", fontSize: "1rem", flexShrink: 0 }}>arrow_forward</span>
                          <span style={{ color: "#6b7280", flex: 1, fontSize: ".875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.subadmin_name}</span>
                          <button onClick={() => handleRemoveUserAssignment(a.id)} style={{ width: 32, height: 32, borderRadius: "8px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", flexShrink: 0 }}><span className="ms" style={{ fontSize: ".875rem" }}>delete</span></button>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* ════════ DEVICES ════════ */}
            {page === "devices" && (
              <div>
                <h2 className="sect-title">Medical Devices</h2>
                <p className="sect-sub">Monitor and link devices to patients.</p>
                <div style={{ display: "flex", gap: ".75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                  <div style={{ position: "relative", flex: 1, maxWidth: 340 }}>
                    <span className="ms" style={{ position: "absolute", left: ".875rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1.125rem" }}>search</span>
                    <input className="inp" style={{ paddingLeft: "2.75rem" }} placeholder="Search by UID or patient…" value={deviceSearch} onChange={e => { setDeviceSearch(e.target.value); setDevicePage(1); }} />
                  </div>
                </div>
                {deviceLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
                    <div style={{ width: 44, height: 44, border: "4px solid #dbeafe", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  </div>
                ) : (
                  <div className="cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" }}>
                    {devices.length === 0 && <p style={{ color: "#9ca3af" }}>No devices found.</p>}
                    {devices.map(d => (
                      <div key={d.id} className="card hovcard" style={{ padding: "1.25rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: ".875rem", marginBottom: "1rem" }}>
                          <div style={{ width: 44, height: 44, borderRadius: "14px", background: d.status === "connected" ? "#f0fdf4" : "#f3f4f6", color: d.status === "connected" ? "#16a34a" : "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><span className="ms">devices</span></div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontWeight: 700, color: "#111827", margin: 0, fontFamily: "monospace", fontSize: ".875rem" }}>{d.uid}</p>
                            <span className="badge" style={{ background: d.status === "connected" ? "#f0fdf4" : "#f3f4f6", color: d.status === "connected" ? "#166534" : "#6b7280", marginTop: ".25rem" }}>
                              <span style={{ width: 5, height: 5, borderRadius: "50%", background: d.status === "connected" ? "#22c55e" : "#9ca3af" }} />{d.status}
                            </span>
                          </div>
                        </div>
                        <div style={{ fontSize: ".8125rem", color: "#6b7280", marginBottom: ".375rem", display: "flex", alignItems: "center", gap: ".375rem" }}><span className="ms" style={{ fontSize: ".875rem" }}>person</span>{d.patient_name || <em style={{ color: "#9ca3af" }}>Unlinked</em>}</div>
                        <div style={{ fontSize: ".8125rem", color: "#9ca3af", marginBottom: ".875rem", display: "flex", alignItems: "center", gap: ".375rem" }}><span className="ms" style={{ fontSize: ".875rem" }}>schedule</span>{fmtDate(d.last_seen)}</div>
                        <button onClick={() => { setLinkModalDevice(d); setLinkPatientId(d.patient_id ? String(d.patient_id) : ""); }} className="pill" style={{ width: "100%", background: "#eff6ff", color: "#2563eb", justifyContent: "center" }}>
                          <span className="ms" style={{ fontSize: ".9rem" }}>link</span>{d.patient_name ? "Reassign" : "Link Patient"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {totalDevPages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", gap: ".5rem", marginTop: "1.5rem", flexWrap: "wrap" }}>
                    {Array.from({ length: totalDevPages }, (_, i) => (
                      <button key={i + 1} onClick={() => setDevicePage(i + 1)} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".875rem", background: devicePage === i + 1 ? "#3b82f6" : "#f3f4f6", color: devicePage === i + 1 ? "#fff" : "#6b7280", fontFamily: "inherit" }}>{i + 1}</button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ════════ CHAT ════════ */}
            {page === "chat" && (
              <div>
                <div style={{ marginBottom: "1.5rem" }}>
                  <h2 className="sect-title">Hospital Chat</h2>
                  <p className="sect-sub">Private messaging with all hospital staff.</p>
                </div>
                {adminHospitalId
                  ? <HospitalChat userId={adminUserId} hospitalId={adminHospitalId} />
                  : <div className="card" style={{ padding: "4rem", textAlign: "center" }}><p style={{ color: "#9ca3af" }}>Hospital information not available.</p></div>
                }
              </div>
            )}

          </div>
        </main>
      </div>

      {/* ══ BOTTOM NAV (mobile only) ══ */}
      <nav className="bottom-nav">
        <div style={{ display: "flex", height: 60 }}>
          {bottomNavWithBadges.map(n => (
            <button
              key={n.id}
              onClick={() => setPage(n.id)}
              style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 2, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit",
                fontSize: ".58rem", fontWeight: 700, color: page === n.id ? "#2563eb" : "#9ca3af",
                padding: 0, position: "relative", transition: "color .15s",
              }}
            >
              <span className="ms" style={{ fontSize: "1.3rem" }}>{n.icon}</span>
              <span>{n.label}</span>
              {n.badge > 0 && (
                <span style={{ position: "absolute", top: 6, right: "calc(50% - 20px)", background: n.id === "chat" ? "#8b5cf6" : "#f59e0b", color: "#fff", fontSize: ".55rem", fontWeight: 700, padding: "1px 5px", borderRadius: "9999px", lineHeight: 1.4 }}>
                  {n.badge}
                </span>
              )}
            </button>
          ))}
          {/* More button for Users & Assignments */}
          <button
            onClick={() => {
              // Cycle through hidden pages
              const hidden = ["users", "assignments"];
              const curr = hidden.indexOf(page);
              setPage(curr >= 0 ? hidden[(curr + 1) % hidden.length] : "users");
            }}
            style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 2, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit",
              fontSize: ".58rem", fontWeight: 700, color: ["users","assignments"].includes(page) ? "#2563eb" : "#9ca3af",
              padding: 0, transition: "color .15s",
            }}
          >
            <span className="ms" style={{ fontSize: "1.3rem" }}>more_horiz</span>
            <span>{["users","assignments"].includes(page) ? (page === "users" ? "Staff" : "Assign") : "More"}</span>
            {(notActivatedU) > 0 && !["users","assignments"].includes(page) && (
              <span style={{ position: "absolute", top: 6, right: "calc(50% - 20px)", background: "#f59e0b", color: "#fff", fontSize: ".55rem", fontWeight: 700, padding: "1px 5px", borderRadius: "9999px", lineHeight: 1.4 }}>
                {notActivatedU}
              </span>
            )}
          </button>
          <button
            onClick={handleLogout}
            style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 2, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit",
              fontSize: ".58rem", fontWeight: 700, color: "#ef4444", padding: 0, transition: "color .15s",
            }}
          >
            <span className="ms" style={{ fontSize: "1.3rem" }}>logout</span>
            <span>Logout</span>
          </button>
        </div>
      </nav>

      {/* ══ MODALS ══ */}

      {addSubAdminModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 480, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Add Sub-Admin</h2>
              <button onClick={() => setAddSubAdminModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: "1rem" }}>close</span></button>
            </div>
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: "1.25rem", display: "flex", alignItems: "flex-start", gap: ".75rem" }}>
              <span className="ms" style={{ color: "#2563eb", fontSize: "1.125rem", flexShrink: 0, marginTop: "1px" }}>info</span>
              <p style={{ fontSize: ".8125rem", color: "#1e40af", margin: 0, lineHeight: 1.5 }}>Account starts as <strong>Not Activated</strong>. Activate it after creation so they can login.</p>
            </div>
            <form onSubmit={async e => {
              e.preventDefault(); const f = e.target;
              const res = await fetch(`${API}/subadmins`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ username: f.username.value, email: f.email.value, password: f.password.value, hospitalId: adminHospitalId })) });
              const d = await res.json(); if (!res.ok) { alert(d.message); return; }
              fetchSubAdmins(); setAddSubAdminModal(false);
            }} style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
              {[["username", "Username", "text"], ["email", "Email", "email"], ["password", "Password", "password"]].map(([n, l, t]) => (
                <div key={n}>
                  <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>{l}</label>
                  <input name={n} type={t} required className="inp" placeholder={l} />
                </div>
              ))}
              <div style={{ display: "flex", gap: ".75rem", marginTop: ".25rem" }}>
                <button type="submit" className="bblu" style={{ flex: 1, justifyContent: "center" }}>Add</button>
                <button type="button" onClick={() => setAddSubAdminModal(false)} className="bgry" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editSubAdminModal && (
        <EditUserModal
          title="Edit Sub-Admin"
          accentBg="#eff6ff"
          accentColor="#2563eb"
          form={editSAForm}
          setForm={setEditSAForm}
          onSave={handleEditSubAdmin}
          onClose={() => setEditSubAdminModal(null)}
        />
      )}

      {viewSubAdminModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 420, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Sub-Admin Details</h2>
              <button onClick={() => setViewSubAdminModal(null)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: "1rem" }}>close</span></button>
            </div>
            {[["Username", viewSubAdminModal.username], ["Email", viewSubAdminModal.email], ["Role", viewSubAdminModal.role], ["Status", viewSubAdminModal.is_active ? "Activated" : "Not Activated"]].map(([l, v]) => (
              <div key={l} style={{ background: "#f9fafb", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: ".625rem" }}>
                <p style={{ fontSize: ".7rem", color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", margin: "0 0 .25rem" }}>{l}</p>
                <p style={{ fontWeight: 600, color: "#111827", margin: 0 }}>{v}</p>
              </div>
            ))}
            <div style={{ display: "flex", gap: ".75rem", marginTop: ".75rem" }}>
              <button onClick={() => { setViewSubAdminModal(null); openEditSubAdmin(viewSubAdminModal); }} className="bblu" style={{ flex: 1, justifyContent: "center" }}>
                <span className="ms" style={{ fontSize: "1rem" }}>edit</span>Edit
              </button>
              <button onClick={() => setViewSubAdminModal(null)} className="bgry" style={{ flex: 1 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {addUserModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 480, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Add Medical Staff</h2>
              <button onClick={() => setAddUserModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: "1rem" }}>close</span></button>
            </div>
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: "1.25rem", display: "flex", alignItems: "flex-start", gap: ".75rem" }}>
              <span className="ms" style={{ color: "#2563eb", fontSize: "1.125rem", flexShrink: 0, marginTop: "1px" }}>info</span>
              <p style={{ fontSize: ".8125rem", color: "#1e40af", margin: 0, lineHeight: 1.5 }}>Account starts as <strong>Not Activated</strong>. Activate it after creation so they can login.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
              {[["Username", "username", "text"], ["Email", "email", "email"], ["Password", "password", "password"]].map(([l, k, t]) => (
                <div key={k}>
                  <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>{l}</label>
                  <input type={t} className="inp" placeholder={l} value={newUser[k]} onChange={e => setNewUser({ ...newUser, [k]: e.target.value })} />
                </div>
              ))}
              <div style={{ display: "flex", gap: ".75rem", marginTop: ".25rem" }}>
                <button onClick={async () => {
                  const { username, email, password } = newUser; if (!username || !email || !password) { alert("All fields required"); return; }
                  const res = await fetch(`${API}/users`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ username, email, password, hospitalId: adminHospitalId })) });
                  const d = await res.json(); if (!res.ok) { alert(d.message); return; }
                  fetchUsers(); setAddUserModal(false); setNewUser({ username: "", email: "", password: "" });
                }} className="bblu" style={{ flex: 1, justifyContent: "center" }}>Add</button>
                <button onClick={() => setAddUserModal(false)} className="bgry" style={{ flex: 1 }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editUserModal && (
        <EditUserModal
          title="Edit Medical Staff"
          accentBg="#f0fdf4"
          accentColor="#16a34a"
          form={editUForm}
          setForm={setEditUForm}
          onSave={handleEditUser}
          onClose={() => setEditUserModal(null)}
        />
      )}

      {viewUserModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 420, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Medical Staff Details</h2>
              <button onClick={() => setViewUserModal(null)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: "1rem" }}>close</span></button>
            </div>
            {[["Username", viewUserModal.username], ["Email", viewUserModal.email], ["Role", viewUserModal.role], ["Status", viewUserModal.is_active ? "Activated" : "Not Activated"]].map(([l, v]) => (
              <div key={l} style={{ background: "#f9fafb", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: ".625rem" }}>
                <p style={{ fontSize: ".7rem", color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", margin: "0 0 .25rem" }}>{l}</p>
                <p style={{ fontWeight: 600, color: "#111827", margin: 0 }}>{v}</p>
              </div>
            ))}
            <div style={{ display: "flex", gap: ".75rem", marginTop: ".75rem" }}>
              <button onClick={() => { setViewUserModal(null); openEditUser(viewUserModal); }} className="bblu" style={{ flex: 1, justifyContent: "center" }}>
                <span className="ms" style={{ fontSize: "1rem" }}>edit</span>Edit
              </button>
              <button onClick={() => setViewUserModal(null)} className="bgry" style={{ flex: 1 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {addPatientModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 480, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Add Patient</h2>
              <button onClick={() => setAddPatientModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: "1rem" }}>close</span></button>
            </div>
            <form onSubmit={async e => {
              e.preventDefault(); const f = e.target;
              const res = await fetch(`${API}/patients`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ hospital_id: adminHospitalId, name: f.name.value, email: f.email.value || null, dob: f.dob.value, created_by_admin_id: adminUserId })) });
              const d = await res.json(); if (!res.ok) { alert(d.error); return; }
              fetchPatients(); setAddPatientModal(false);
            }} style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
              {[["name", "Full Name", "text", true], ["email", "Email (optional)", "email", false]].map(([n, l, t, r]) => (
                <div key={n}>
                  <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>{l}</label>
                  <input name={n} type={t} required={r} className="inp" placeholder={l} />
                </div>
              ))}
              <div>
                <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Date of Birth</label>
                <input name="dob" type="date" required className="inp" />
              </div>
              <div style={{ display: "flex", gap: ".75rem", marginTop: ".25rem" }}>
                <button type="submit" className="bblu" style={{ flex: 1, justifyContent: "center" }}>Add</button>
                <button type="button" onClick={() => setAddPatientModal(false)} className="bgry" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewPatientModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 440, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Patient Details</h2>
              <button onClick={() => setViewPatientModal(null)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: "1rem" }}>close</span></button>
            </div>
            {[["Name", viewPatientModal.name], ["Email", viewPatientModal.email || "—"], ["DOB", fmtDob(viewPatientModal.dob)], ["Created By", viewPatientModal.created_by_username || "—"], ["Created At", fmtDate(viewPatientModal.created_at)]].map(([l, v]) => (
              <div key={l} style={{ background: "#f9fafb", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: ".625rem" }}>
                <p style={{ fontSize: ".7rem", color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", margin: "0 0 .25rem" }}>{l}</p>
                <p style={{ fontWeight: 600, color: "#111827", margin: 0 }}>{v}</p>
              </div>
            ))}
            <button onClick={() => setViewPatientModal(null)} className="bgry" style={{ width: "100%", marginTop: ".75rem" }}>Close</button>
          </div>
        </div>
      )}

      {editPatientModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 480, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Edit Patient</h2>
              <button onClick={() => setEditPatientModal(null)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: "1rem" }}>close</span></button>
            </div>
            <form onSubmit={async e => {
              e.preventDefault(); const f = e.target;
              const res = await fetch(`${API}/patients/${editPatientModal.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({ name: f.name.value, email: f.email.value || null, dob: f.dob.value })) });
              if (!res.ok) { const d = await res.json(); alert(d.error); return; }
              fetchPatients(); setEditPatientModal(null);
            }} style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
              {[["name", "Full Name", "text", editPatientModal.name], ["email", "Email", "email", editPatientModal.email || ""]].map(([n, l, t, dv]) => (
                <div key={n}>
                  <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>{l}</label>
                  <input name={n} type={t} defaultValue={dv} className="inp" />
                </div>
              ))}
              <div>
                <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Date of Birth</label>
                <input name="dob" type="date" required defaultValue={editPatientModal.dob ? editPatientModal.dob.split("T")[0] : ""} className="inp" />
              </div>
              <div style={{ display: "flex", gap: ".75rem", marginTop: ".25rem" }}>
                <button type="submit" className="bblu" style={{ flex: 1, justifyContent: "center" }}>Save Changes</button>
                <button type="button" onClick={() => setEditPatientModal(null)} className="bgry" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {linkModalDevice && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 440, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Link Device to Patient</h2>
              <button onClick={() => { setLinkModalDevice(null); setLinkPatientId(""); }} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}><span className="ms" style={{ fontSize: "1rem" }}>close</span></button>
            </div>
            <div style={{ background: "#f9fafb", borderRadius: "1rem", padding: "1rem", marginBottom: "1.25rem" }}>
              <p style={{ fontSize: ".7rem", color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", margin: "0 0 .25rem" }}>Device UID</p>
              <p style={{ fontWeight: 700, color: "#111827", margin: 0, fontFamily: "monospace" }}>{linkModalDevice.uid}</p>
            </div>
            <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".5rem" }}>Assign to Patient</label>
            <select className="inp" value={linkPatientId} onChange={e => setLinkPatientId(e.target.value)} style={{ marginBottom: "1.25rem" }}>
              <option value="">— Unlink / No Patient —</option>
              {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: "-.75rem 0 1rem" }}>Select "No Patient" to unlink this device.</p>
            <div style={{ display: "flex", gap: ".75rem" }}>
              <button onClick={handleLinkDevice} className="bblu" style={{ flex: 1, justifyContent: "center" }}>{linkPatientId ? "Link" : "Unlink"}</button>
              <button onClick={() => { setLinkModalDevice(null); setLinkPatientId(""); }} className="bgry" style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalAdminDashboard;